import { AngularReactDialog } from "@omnia/types"
import { handleApiError } from "../../error"
import { getCropCssColor } from "../../fields"
import { ReactAngularComponentContext } from "../../react/angular"
import { Checkbox } from "../../react/checkbox"
import { CropLabel } from "../../react/CropLabel"
import { DialogBody } from "../../react/DialogBody"
import { DialogFooter, DialogFooterButtons } from "../../react/DialogFooter"
import { DialogHeader } from "../../react/DialogHeader"
import { Msg } from "../../react/Msg"
import { IOmniaShell } from "../../shell"
import { SharedFieldsService } from "../service"
import { EditContext } from "./editContext"
import { RowWithListColumns } from "./RowWithListColumns"
import { Target } from "./target"
import { TargetCategory } from "./targetCategory"

export const ReactSetupCropYearDialog: AngularReactDialog = {

    inject: {
        shell: "OmniaShell",
        fieldSvc: "OmniaFieldsService",
    },

    component: SetupCropYearDialog

}


/**
 * Dialog to setup a cropping year & create new uncropped areas for fields with no cropping information.
 * @param dependencies: the dependencies injected by Angular
 * @param close: the function to close the dialog
 * @param state: the initial state
 * @returns  the React component
 */
export function SetupCropYearDialog({ dependencies, close }: ReactAngularComponentContext<{}>) {

    const shell: IOmniaShell = dependencies["shell"];
    const fieldSvc: SharedFieldsService = dependencies["fieldSvc"];

    const [{ targetYear, fieldsEditContext }, setState] = React.useState(() => {
        const session = shell.getSession();
        const targetYear = fieldSvc.yearId;
        const fieldsEditContext = new EditContext(targetYear, fieldSvc, true, true);

        return {
            session,
            targetYear,
            fieldsEditContext,
        };
    });

    const forceRefreshState = React.useCallback(() => setState((prev) => ({ ...prev })), []);

    const categories = TargetCategory.create(fieldsEditContext.targets);

    const [isCommitting, setIsCommitting] = React.useState(false);
    const canCommit = !isCommitting && fieldsEditContext.canCommit();

    /**
     * Commit the operation, and then close the dialog.
     */
    const commit = React.useCallback(() => {
        if (!canCommit) return;
        setIsCommitting(true);

        const model = fieldsEditContext.getCroppingYearModel(false);

        fieldSvc
            .setupCroppingYear(model)
            .then(() => close())
            .catch((r) =>
                handleApiError(shell, r, { defaultMessage: "An error has occurred while updating the crops" })
            )
            .finally(() => {
                setIsCommitting(false);
            });
    }, [fieldsEditContext, canCommit, fieldSvc, close, shell]);

    return (
        <div className="omnia-fields-setup-crop-year-dialog">
            <DialogHeader icon="calendar">
                <Msg text="Set up cropping year {{targetYear}}" args={{ targetYear }} />
            </DialogHeader>
            <DialogBody>
                <div className="prompt">
                    <p>
                        <Msg
                            text="This will create new uncropped areas for all the fields with no cropping information on year {{targetYear}}."
                            args={{ targetYear }}
                        />
                    </p>
                    <p>
                        <Msg text="Please select the target fields from the list below." />
                    </p>
                </div>
                <Crops categories={categories} onChange={forceRefreshState} readOnly={false} />
            </DialogBody>
            <DialogFooter>
                <DialogFooterButtons
                    onClose={close}
                    hasChanges={true}
                    canCommit={canCommit}
                    onCommit={commit}
                />
            </DialogFooter>
        </div>
    );
}

type CropsProps = {
    categories: TargetCategory[];
    readOnly: boolean;
    onChange: () => void;
};

const Crops = ({ categories, readOnly = false, onChange }: CropsProps) => {
    const visibleCategories = categories.filter((x) => x.visible || readOnly);

    const toggleSelection = React.useCallback(
        (targets: Target[]) => {
            const selectableTargets = targets.filter((x) => x.selectable);
            const nextValue = !selectableTargets.some((x) => x.selected);
            selectableTargets.forEach((x) => (x.selected = nextValue));
            onChange();
        },
        [onChange]
    );

    return (
        <>
            {visibleCategories.map(({ title, targets }) => {
                const selectableTargets = targets.filter((x) => x.selectable);
                const visibleTargets = targets.filter((x) => x.visible && (!readOnly || x.selected));
                return (
                    <section key={title}>
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col col-md-12">
                                    <h4>
                                        {readOnly ? (
                                            title
                                        ) : (
                                            <Checkbox
                                                checked={selectableTargets.some((x) => x.selected)}
                                                indeterminate={selectableTargets.some(
                                                    (x) => x.selectable && !x.selected
                                                )}
                                                disabled={visibleTargets.every((x) => !x.selectable)}
                                                iconDisabled="fas fa-ban"
                                                setChecked={() => toggleSelection(visibleTargets)}
                                            >
                                                {title}
                                            </Checkbox>
                                        )}
                                    </h4>
                                </div>
                            </div>

                            {visibleTargets.length ? (
                                <RowWithListColumns
                                    className="targets"
                                    items={visibleTargets}
                                    columns={2}
                                    renderItem={(t) => {
                                        const label = (
                                            <CropLabel
                                                color={getCropCssColor(t.crop)}
                                                title={t.description}
                                            />
                                        );
                                        return readOnly ? (
                                            label
                                        ) : (
                                            <Checkbox
                                                cssClass="target"
                                                checked={t.selected}
                                                disabled={!t.selectable}
                                                iconDisabled="fas fa-ban"
                                                setChecked={() => toggleSelection([t])}
                                            >
                                                {label}
                                            </Checkbox>
                                        );
                                    }}
                                />
                            ) : (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <p>
                                            <Msg text="No cropped areas will be created." />
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                );
            })}
        </>
    );
};

SetupCropYearDialog.Crops = Crops;
